import { getFormattedMoney, roundToDecimals, getNumberFromInput } from '@/components/thanks-page/utils/helpers';

export interface DebtAnalysisRaw {
  isCalculationValid: boolean;
  term: number;
  monthlyRate: number;
  garnishmentAmount: number;
  totalRepayment: number;
  eingabeB6: number;
}

interface DebtAnalysis {
  isCalculationValid: string;
  term: number;
  monthlyRate: string;
  garnishmentAmount: string;
  potentialSavingDeEur: string;
}

// Pfändungsfreigrenzen calculation - single source of truth, used both for the thanks-page
// display and for the `data_sa` payload persisted on submit (see app.jsx). Keep the constants
// here in sync with whatever legal thresholds are currently in effect - see commit history for
// "pfändungsgrenzen" updates.
export function calculateDebtAnalysisRaw(answeredSlides: Record<string, string>): DebtAnalysisRaw {
  const calculatedEingabeB6 = getNumberFromInput(answeredSlides['gesamtschulden']);

  const eingabeB5 = (): number => {
    if (answeredSlides['kinder'] && (answeredSlides['kinder'] === 'Nein' || answeredSlides['kinder'] === 'Sonstiges')) {
      return 0;
    }
    return getNumberFromInput(answeredSlides['kinderanzahl']);
  };

  const eingabeB1 = (): number => {
    return getNumberFromInput(answeredSlides['einkommen']);
  };

  const eingabeB2 = (): number => {
    return getNumberFromInput(answeredSlides['einkommen-ehegatte']);
  };

  const eingabeB3 = (): string => {
    return answeredSlides['familienstand'] ? answeredSlides['familienstand'] : 'Ledig';
  };

  const eingabeB4 = (): string => {
    return answeredSlides['veranschlagung'] && answeredSlides['veranschlagung'] === 'Gemeinsam'
      ? 'Gemeinsam'
      : 'Einzeln';
  };

  const berechnungB2 = (): number => {
    return Math.min(eingabeB1() * 0.1, 486);
  };

  const berechnungB3 = (): number => {
    return Math.min(eingabeB2() * 0.1, 486);
  };

  const stepsB4 = (): number => 0;
  const stepsC4 = (): number => 60;
  const stepsD4 = (): number => 93;
  const stepsE4 = (): number => 127;
  const stepsF4 = (): number => 160;
  const stepsG4 = (): number => 193;

  const stepsB2 = (): number => 7;
  const stepsC2 = (): number => 5;
  const stepsD2 = (): number => 4;
  const stepsE2 = (): number => 3;
  const stepsF2 = (): number => 2;
  const stepsG2 = (): number => 1;

  const stepsB3 = (): number => 1.82;
  const stepsC3 = (): number => 2.59;
  const stepsD3 = (): number => 0.94;
  const stepsE3 = (): number => 2.86;
  const stepsF3 = (): number => 1.34;
  const stepsG3 = (): number => 0.39;

  const berechnungB8 = (): 0 | 1 => {
    if (
      (eingabeB3() === 'Verheiratet' || eingabeB3() === 'Lebenspartnerschaft') &&
      eingabeB4() !== 'Gemeinsam' &&
      eingabeB2() <= 700
    ) {
      return 1;
    }
    if (eingabeB4() === 'Gemeinsam' && (eingabeB2() <= 700 || eingabeB1() <= 700)) {
      return 1;
    }
    return 0;
  };

  const berechnungB9 = (): number => {
    return Math.min(berechnungB8() + eingabeB5(), 5);
  };

  const berechnungD12 = (): number => (berechnungB9() === 1 ? stepsC4() : 0);
  const berechnungE12 = (): number => (berechnungB9() === 2 ? stepsD4() : 0);
  const berechnungF12 = (): number => (berechnungB9() === 3 ? stepsE4() : 0);
  const berechnungG12 = (): number => (berechnungB9() === 4 ? stepsF4() : 0);
  const berechnungH12 = (): number => (berechnungB9() === 5 ? stepsG4() : 0);
  const berechnungC12 = (): number => (berechnungB9() === 0 ? stepsB4() : 0);

  const berechnungB12 = (): number => {
    return berechnungC12() + berechnungD12() + berechnungE12() + berechnungF12() + berechnungG12() + berechnungH12();
  };

  const berechnungB14 = (): number => {
    return berechnungB2() - (berechnungB12() + 159);
  };

  const berechnungC10 = (): number => (berechnungB9() === 0 ? stepsB2() : 0);
  const berechnungD10 = (): number => (berechnungB9() === 1 ? stepsC2() : 0);
  const berechnungE10 = (): number => (berechnungB9() === 2 ? stepsD2() : 0);
  const berechnungF10 = (): number => (berechnungB9() === 3 ? stepsE2() : 0);
  const berechnungG10 = (): number => (berechnungB9() === 4 ? stepsF2() : 0);
  const berechnungH10 = (): number => (berechnungB9() === 5 ? stepsG2() : 0);

  const berechnungB10 = (): number => {
    return berechnungC10() + berechnungD10() + berechnungE10() + berechnungF10() + berechnungG10() + berechnungH10();
  };

  const berechnungB5 = (): number => {
    const result = eingabeB1() - 4859.99;
    return result < 0 ? 0 : result;
  };

  const berechnungC11 = (): number => (berechnungB9() === 0 ? stepsB3() : 0);
  const berechnungD11 = (): number => (berechnungB9() === 1 ? stepsC3() : 0);
  const berechnungE11 = (): number => (berechnungB9() === 2 ? stepsD3() : 0);
  const berechnungF11 = (): number => (berechnungB9() === 3 ? stepsE3() : 0);
  const berechnungG11 = (): number => (berechnungB9() === 4 ? stepsF3() : 0);
  const berechnungH11 = (): number => (berechnungB9() === 5 ? stepsG3() : 0);

  const berechnungB11 = (): number => {
    return berechnungC11() + berechnungD11() + berechnungE11() + berechnungF11() + berechnungG11() + berechnungH11();
  };

  const berechnungB6 = (): number => {
    const result = eingabeB2() - 4859.99;
    return result < 0 ? 0 : result;
  };

  const berechnungB15 = (): number => {
    return berechnungB3() - (berechnungB12() + 159);
  };

  const berechnungB18 = (): number => {
    let rv1 = 0;
    if (berechnungB3() >= 159 + berechnungB12()) {
      rv1 = berechnungB15() * berechnungB10() + berechnungB6() + berechnungB11();
    }
    return Math.max(rv1, 0);
  };

  const berechnungB17 = (): number => {
    let rv = 0;
    if (eingabeB1() >= 159 + berechnungB12()) {
      rv = berechnungB14() * berechnungB10() + berechnungB5() + berechnungB11();
    }
    return Math.max(rv, 0);
  };

  const berechnungB20 = (): number => {
    if (eingabeB4() === 'Einzeln') {
      return berechnungB17();
    }
    return berechnungB17() + berechnungB18();
  };

  const eingabeB8 = (): number => berechnungB20();
  const eingabeB9 = (): number => 50;
  const eingabeB15 = (): number => 36;
  const eingabeB6 = (): number => calculatedEingabeB6;

  const eingabeB10 = (): number => {
    const val = eingabeB6() * (0.3 / 36) + eingabeB9();
    if (val >= eingabeB8()) {
      return val;
    }
    return eingabeB8();
  };

  const eingabeB11 = (): number => {
    const val = (eingabeB10() - eingabeB9()) * eingabeB15();
    if (val <= eingabeB6()) {
      return val;
    }
    return eingabeB6();
  };

  const eingabeB7 = (): number => {
    if (eingabeB11() === eingabeB6()) {
      return eingabeB10() > 0 ? eingabeB11() / eingabeB10() : 0;
    }
    return 36;
  };

  const eingabeC10 = (): number => {
    if (eingabeB10() > eingabeB6()) {
      return eingabeB6() + eingabeB9();
    }
    return eingabeB10();
  };

  return {
    isCalculationValid: eingabeC10() > 0,
    term: eingabeB7(),
    monthlyRate: eingabeC10(),
    garnishmentAmount: eingabeB8(),
    // Eingabe!B11 - the sheet's own total repayment, already capped at Schuldsumme (eingabeB6)
    // whenever the garnishable amount would otherwise overpay the debt. Source of truth for
    // "what the customer actually pays back" - see calculateDebtAnalysis() below.
    totalRepayment: eingabeB11(),
    eingabeB6: calculatedEingabeB6,
  };
}

export function calculateDebtAnalysis(answeredSlides: Record<string, string>): DebtAnalysis {
  const raw = calculateDebtAnalysisRaw(answeredSlides);
  const roundedTerm = Math.round(raw.term);
  const roundedMonthlyRate = roundToDecimals(raw.monthlyRate);
  // Savings must match the sheet exactly (Eingabe!B6 - Eingabe!B11), including the "fully repaid"
  // case where B11 === B6 and the saving is 0. Do NOT recompute this from the rounded display
  // rate x rounded term - that drifts from the sheet whenever rounding moves the product away
  // from the already-correct, capped raw.totalRepayment.
  const potentialSaving = Math.max(Math.floor(raw.eingabeB6) - Math.floor(raw.totalRepayment), 0);

  return {
    isCalculationValid: getFormattedMoney(raw.isCalculationValid ? raw.monthlyRate : 1),
    term: roundedTerm,
    monthlyRate: getFormattedMoney(roundedMonthlyRate),
    garnishmentAmount: getFormattedMoney(raw.garnishmentAmount),
    potentialSavingDeEur: getFormattedMoney(potentialSaving),
  };
}
